import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Info } from "@/assets/icons/icons";
import { Toggle } from "@/components/Interface/Toggle/Toggle";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { useAlert } from "@/hooks/useAlert";
import { useIsInSecuritySubFlow } from "@/hooks/useIsInSecuritySubFlow";
import { useChangeMagicLinkPermission, useGetCustomer } from "@/services/api";
import { BasicCustomerDataTwoFactorMethod } from "@/services/model";
import { AlertTypes, userAccountStateAtom } from "@/utils/atoms";
import {
  isMagicLoginNotAllowedError,
  magicLinkErrorKeys,
} from "@/utils/magicLinkErrors";
import { getMagicLoginEnabled } from "@/utils/magicLoginPermission";

export const MagicLinkLoginSetting = () => {
  const { t } = useTranslation("portal");
  const { t: tMagicLinkError } = useTranslation();
  const showAlert = useAlert();
  const userAccountState = useAtomValue(userAccountStateAtom);
  const isEditingTwoFactor = useIsInSecuritySubFlow();
  const [optimisticEnabled, setOptimisticEnabled] = useState<boolean | null>(
    null,
  );

  const {
    data: customer,
    isLoading: isLoadingCustomer,
    refetch,
  } = useGetCustomer({ expanded: true });

  // Read straight from the customer instead of `useTwoFactorMethodFromQueryParam`:
  // that hook prefers the `expectedAuthType` query param over the backend state
  // (it exists to bridge a pending 2FA switch), which would lock this toggle for
  // a customer who has no 2FA. Unknown while loading counts as "not enabled".
  const hasTwoFactorEnabled =
    !!customer?.twoFactorMethod &&
    customer.twoFactorMethod !== BasicCustomerDataTwoFactorMethod.STANDARD;

  const { mutate: changeMagicLoginPermission, isLoading: isUpdating } =
    useChangeMagicLinkPermission({
      mutation: {
        onSuccess: async () => {
          await refetch();
          setOptimisticEnabled(null);
          showAlert({
            type: AlertTypes.success,
            text: t("user-account.security.magicLink.saveSuccess"),
          });
        },
        onError: (error) => {
          setOptimisticEnabled(null);

          showAlert({
            type: AlertTypes.error,
            text: isMagicLoginNotAllowedError(error)
              ? tMagicLinkError(magicLinkErrorKeys.notAllowed)
              : t("user-account.security.magicLink.saveError"),
          });
        },
      },
    });

  useEffect(() => {
    setOptimisticEnabled(null);
  }, [customer]);

  const isEnabled = optimisticEnabled ?? getMagicLoginEnabled(customer);
  const isDisabled =
    isLoadingCustomer ||
    isUpdating ||
    hasTwoFactorEnabled ||
    userAccountState.editingMail ||
    userAccountState.editingPassword ||
    isEditingTwoFactor;

  const handleToggle = () => {
    if (isDisabled) {
      return;
    }

    const nextEnabled = !isEnabled;
    setOptimisticEnabled(nextEnabled);
    changeMagicLoginPermission({ params: { enabled: nextEnabled } });
  };

  return (
    <>
      {(isLoadingCustomer || isUpdating) && <LoadingSpinner />}
      <ProfileCard
        dataTestid="magic-link-login-setting"
        title={t("user-account.security.magicLink.title")}
        description={t("user-account.security.magicLink.description")}
        stickyHeader={false}
        maxWidth="none"
        actions={
          <Toggle
            enabled={isEnabled}
            onChange={handleToggle}
            disabled={isDisabled}
            ariaLabel={t("user-account.security.magicLink.toggleTitle")}
            size="sm"
          />
        }
      >
        {hasTwoFactorEnabled && (
          <div className="flex items-center gap-2 rounded-md bg-surface-tertiary p-4">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-surface-info">
              <Info className="h-4 w-4 text-foreground-primary-inverse" />
            </span>
            <span className="text-base font-semibold text-foreground-primary">
              {t("user-account.security.magicLink.disabledBecauseTwoFactor")}
            </span>
          </div>
        )}
      </ProfileCard>
    </>
  );
};
